// Platform Admin — Phase 4 overview (ADMIN_UPGRADE_PLAN 45, 46, 11, 43).
// The daily pulse: headline numbers, things needing action, a usage
// leaderboard, churn-risk flags, and a cross-tenant phone lookup.
import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import {
  overview, usageLeaderboard, churnRisk, findConversation,
  type Overview, type LeaderRow, type ChurnRow, type ConvMatch,
} from '../../lib/padminAnalytics';

const CARD = 'rounded-card border border-slate-200 bg-white p-5 shadow-sm';
const GHOST = 'rounded-[8px] border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-ink-2 hover:border-primary hover:text-primary disabled:opacity-40';
const FLD = 'w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary';
const TH = 'whitespace-nowrap bg-surface px-3.5 py-3 text-start text-[11px] font-bold uppercase tracking-wide text-ink-3';

const usd = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const when = (ms: number) => (ms ? new Date(ms).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

function Delta({ pct }: { pct: number | null }) {
  if (pct === null) return null;
  const up = pct >= 0;
  return (
    <span className={cn('ms-2 text-xs font-bold', up ? 'text-primary' : 'text-red-600')}>
      {up ? '▲' : '▼'} {Math.abs(pct)}%
    </span>
  );
}

function Big({ label, value, delta, tone }: {
  label: string; value: string | number; delta?: number | null; tone?: 'good' | 'bad';
}) {
  return (
    <div className={CARD}>
      <span className="block text-[11px] font-bold uppercase tracking-wide text-ink-3">{label}</span>
      <b className={cn('mt-1 block text-2xl font-extrabold',
        tone === 'good' ? 'text-primary' : tone === 'bad' ? 'text-red-600' : 'text-ink')}>
        {value}{delta !== undefined && <Delta pct={delta ?? null} />}
      </b>
    </div>
  );
}

/** One actionable alert; zero-count alerts render muted rather than hidden. */
function Alert({ label, count, onGo }: { label: string; count: number; onGo?: () => void }) {
  const hot = count > 0;
  return (
    <button
      type="button"
      onClick={onGo}
      disabled={!onGo}
      className={cn('flex items-center gap-2 rounded-[9px] border px-3 py-2 text-start',
        hot ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white',
        onGo && 'hover:border-primary')}
    >
      <b className={cn('text-lg font-extrabold', hot ? 'text-amber-700' : 'text-ink-3')}>{count}</b>
      <span className={cn('text-xs font-semibold', hot ? 'text-amber-800' : 'text-ink-3')}>{label}</span>
    </button>
  );
}

export default function OverviewPanel({ onGoto, onOpenTenant }: {
  onGoto?: (section: string) => void;
  onOpenTenant?: (id: string) => void;
}) {
  const { t } = useI18n();
  const [ov, setOv] = useState<Overview | null>(null);
  const [leaders, setLeaders] = useState<LeaderRow[] | null>(null);
  const [churn, setChurn] = useState<ChurnRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // phone lookup
  const [phone, setPhone] = useState('');
  const [matches, setMatches] = useState<ConvMatch[] | null>(null);
  const [searching, setSearching] = useState(false);

  const load = useCallback(() => {
    setErr(null); setOv(null); setLeaders(null); setChurn(null);
    void overview().then(setOv).catch((e: Error) => setErr(e.message));
    void usageLeaderboard(10).then((r) => setLeaders(r.leaders)).catch(() => setLeaders([]));
    void churnRisk().then((r) => setChurn(r.flagged)).catch(() => setChurn([]));
  }, []);
  useEffect(load, [load]);

  async function search() {
    if (phone.trim().length < 6) return;
    setSearching(true); setErr(null); setMatches(null);
    try {
      const r = await findConversation(phone.trim(), 20);
      setMatches(r.matches);
    } catch (e) { setErr((e as Error).message); } finally { setSearching(false); }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-ink-3">{t('pa.ov.note')}</p>
        <button type="button" onClick={load} className={cn(GHOST, 'ms-auto')}>{t('pa.hl.refresh')}</button>
      </div>
      {err && <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}
      {!ov && <div className="py-10 text-center text-sm text-ink-3">…</div>}

      {ov && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Big label={t('pa.rev.mrr')} value={usd(ov.revenue.mrrUsd)} tone="good" />
            <Big label={t('pa.ov.tenants')} value={ov.tenants.total} />
            <Big label={t('pa.ov.messages')} value={ov.volume.messagesThisMonth.toLocaleString()} delta={ov.volume.changePct} />
            <Big label={t('pa.ov.liveNumbers')} value={`${ov.volume.liveNumbers}/${ov.volume.connectedNumbers}`} />
          </div>

          <div className={CARD}>
            <b className="block text-sm text-ink">{t('pa.ov.needsAction')}</b>
            <div className="mt-3 flex flex-wrap gap-2">
              <Alert label={t('pa.ov.pastDue')} count={ov.alerts.pastDue} onGo={onGoto && (() => onGoto('billing'))} />
              <Alert label={t('pa.ov.trialsExpired')} count={ov.alerts.trialsExpired} onGo={onGoto && (() => onGoto('tenants'))} />
              <Alert label={t('pa.ov.trialsEnding')} count={ov.alerts.trialsEndingSoon} onGo={onGoto && (() => onGoto('tenants'))} />
              <Alert label={t('pa.ov.pendingTemplates')} count={ov.alerts.pendingTemplates} onGo={onGoto && (() => onGoto('templates'))} />
              <Alert label={t('pa.ov.activeBroadcasts')} count={ov.alerts.activeBroadcasts} onGo={onGoto && (() => onGoto('broadcasts'))} />
              <Alert label={t('pa.ov.openTickets')} count={ov.alerts.openTickets} onGo={onGoto && (() => onGoto('tickets'))} />
            </div>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 border-t border-slate-100 pt-3 text-xs text-ink-3">
              <span>{t('pa.ov.new7d')}: <b className="text-ink">{ov.tenants.new7d}</b></span>
              <span>{t('pa.ov.new30d')}: <b className="text-ink">{ov.tenants.new30d}</b></span>
              <span>{t('pa.rev.paying')}: <b className="text-ink">{ov.revenue.paying}</b></span>
              <span>{t('pa.rev.conversion')}: <b className="text-ink">{ov.revenue.conversionPct}%</b></span>
              <span>{t('pa.ov.subscribers')}: <b className="text-ink">{ov.volume.subscribers.toLocaleString()}</b></span>
              <span>{t('pa.rev.arr')}: <b className="text-ink">{usd(ov.revenue.arrUsd)}</b></span>
            </div>
          </div>
        </>
      )}

      {/* Usage leaderboard + churn risk, side by side on wide screens */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className={CARD}>
          <b className="block text-sm text-ink">{t('pa.ov.leaderboard')}</b>
          {leaders === null ? <div className="py-6 text-center text-sm text-ink-3">…</div>
            : leaders.length === 0 ? <p className="mt-2 text-xs text-ink-3">{t('pa.ov.noUsage')}</p>
              : (
                <ul className="mt-2 divide-y divide-slate-100">
                  {leaders.map((r) => (
                    <li key={r.tenantId} className="flex items-center gap-2 py-2">
                      <button type="button" onClick={() => onOpenTenant?.(r.tenantId)}
                        className="min-w-0 flex-1 truncate text-start text-[13px] font-semibold text-ink hover:text-primary hover:underline">
                        {r.tenantName}
                      </button>
                      <span className="text-[11px] text-ink-3">{r.subscribers.toLocaleString()} subs</span>
                      <b className="text-[13px] text-ink">{r.thisMonth.toLocaleString()}</b>
                      <Delta pct={r.changePct} />
                    </li>
                  ))}
                </ul>
              )}
        </div>

        <div className={CARD}>
          <b className="block text-sm text-ink">{t('pa.ov.churn')}</b>
          <p className="text-xs text-ink-3">{t('pa.ov.churnNote')}</p>
          {churn === null ? <div className="py-6 text-center text-sm text-ink-3">…</div>
            : churn.length === 0 ? <p className="mt-2 text-xs text-ink-3">{t('pa.ov.churnEmpty')}</p>
              : (
                <ul className="mt-2 divide-y divide-slate-100">
                  {churn.slice(0, 12).map((r) => (
                    <li key={r.tenantId} className="py-2">
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => onOpenTenant?.(r.tenantId)}
                          className="min-w-0 flex-1 truncate text-start text-[13px] font-semibold text-ink hover:text-primary hover:underline">
                          {r.tenantName}
                        </button>
                        <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold',
                          r.score >= 50 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700')}>
                          {r.score}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {r.reasons.map((k) => (
                          <span key={k} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-ink-2">
                            {t(`pa.churn.${k}`)}
                          </span>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
        </div>
      </div>

      {/* 43. Cross-tenant phone lookup */}
      <div className={CARD}>
        <b className="block text-sm text-ink">{t('pa.ov.lookup')}</b>
        <p className="text-xs text-ink-3">{t('pa.ov.lookupNote')}</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void search(); }}
            placeholder="+968 7691 2321"
            className={cn(FLD, 'max-w-xs')}
          />
          <button type="button" onClick={() => void search()} disabled={searching || phone.trim().length < 6}
            className="rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40">
            {searching ? '…' : t('pa.ov.search')}
          </button>
        </div>

        {matches?.length === 0 && <p className="mt-3 text-sm text-ink-3">{t('pa.ov.noMatch')}</p>}
        {matches?.map((m) => (
          <div key={m.tenantId} className="mt-4 rounded-[9px] border border-slate-200">
            <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3.5 py-2.5">
              <button type="button" onClick={() => onOpenTenant?.(m.tenantId)}
                className="text-[13px] font-bold text-ink hover:text-primary hover:underline">
                {m.tenantName}
              </button>
              <span className="font-mono text-[11px] text-ink-3">{m.subscriber.phoneE164}</span>
              {m.subscriber.name && <span className="text-[11px] text-ink-2">{m.subscriber.name}</span>}
              <span className={cn('ms-auto rounded-full px-2 py-0.5 text-[10px] font-bold',
                m.subscriber.optInStatus === 'subscribed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')}>
                {m.subscriber.optInStatus ?? 'unknown'}
              </span>
            </div>
            {m.messages.length === 0 ? (
              <p className="px-3.5 py-3 text-xs text-ink-3">{t('pa.ov.noMessages')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] border-collapse">
                  <thead>
                    <tr>
                      {['pa.ov.col.when', 'pa.ov.col.dir', 'pa.ov.col.text', 'pa.inv.col.status'].map((k) => (
                        <th key={k} className={TH}>{t(k)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {m.messages.map((msg) => (
                      <tr key={msg.id} className="border-t border-slate-100">
                        <td className="whitespace-nowrap px-3.5 py-2 text-[11px] text-ink-3">{when(msg.createdAt)}</td>
                        <td className="whitespace-nowrap px-3.5 py-2 text-[11px] font-bold text-ink-2">{msg.direction}</td>
                        <td className="px-3.5 py-2 text-[12px] text-ink">{msg.text ?? <i className="text-ink-3">media</i>}</td>
                        <td className="whitespace-nowrap px-3.5 py-2">
                          <span className={cn('text-[11px] font-bold',
                            msg.error ? 'text-red-600' : 'text-ink-2')}>
                            {String(msg.error ?? msg.status ?? '—')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
